import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FileText, Loader2, Printer, Radio, Save } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { writeSanitizedHTML } from '../utils/safePrint';
import PlanEntriesCard from './PlanEntriesCard';

const API = `${API_BASE_URL}/api`;

const FIELDS = [
  { key: 'plan_description', label: 'Plan Description', placeholder: 'general description of comms plan for this event' },
  { key: 'current_situation', label: 'Current Situation', placeholder: 'describe the current situation and how it may affect comms' },
  { key: 'standardized_messaging', label: 'Standardized Messaging', placeholder: 'ensuring accuracy, timeliness, and consistent tone to protect public safety and organizational reputation.' },
];
const CHANNEL_FIELDS = [
  { key: 'communication_methods', label: 'Communication Methods', placeholder: 'describe primary and backup tools (such as secure radio frequencies, satellite phones, or dedicated collaboration platforms)' },
  { key: 'jurisdictional_boundaries', label: 'Jurisdictional Boundaries', placeholder: 'describe or establish routine multi-agency meetings or teleconferences to resolve cross-boundary conflicts.' },
  { key: 'agency_organizations_sector', label: 'Agency / Organizations / Sector', placeholder: 'describe any issues involving agencies, municipal, industrial, healthcare, etc' },
];

const empty = {
  plan_description: '',
  current_situation: '',
  standardized_messaging: '',
  communication_methods: '',
  jurisdictional_boundaries: '',
  agency_organizations_sector: '',
};

const CommsPlanBriefModule = ({ theme, currentExercise, currentPhase = '' }) => {
  const [data, setData] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);

  const exerciseId = currentExercise?.id;

  useEffect(() => {
    if (!exerciseId) { setLoading(false); return; }
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/comms-plan-brief/${exerciseId}`);
        if (!cancel) setData({ ...empty, ...(r.data || {}) });
      } catch (e) {
        console.error('Failed to load comms plan:', e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [exerciseId]);

  const persist = async (next) => {
    if (!exerciseId) return;
    setSaving(true);
    try {
      await axios.put(`${API}/comms-plan-brief/${exerciseId}`, { ...next, exercise_id: exerciseId });
      setSavedAt(new Date());
    } catch (e) {
      console.error('Save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  const onChange = (key, value) => {
    const next = { ...data, [key]: value };
    setData(next);
    // Debounced auto-save (600ms after last keystroke)
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(next), 600);
  };

  const handleSaveNow = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    persist(data);
  };

  const handlePrint = () => {
    const w = window.open('', '_blank');
    const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>');
    const section = (title, items) => `
      <div class="section">
        <h2>${title}</h2>
        ${items.map(f => `
          <div class="field">
            <div class="label">${f.label}</div>
            <div class="value">${data[f.key] ? esc(data[f.key]) : '<span class="empty">Not provided</span>'}</div>
          </div>
        `).join('')}
      </div>
    `;
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Comms Plan — ${esc(currentExercise?.exercise_name || 'Exercise')}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; color: #222; line-height: 1.5; }
            h1 { color: #f97316; border-bottom: 3px solid #f97316; padding-bottom: 8px; margin-bottom: 20px; }
            h2 { color: #f97316; margin: 24px 0 12px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
            .meta { color: #666; font-size: 12px; margin-bottom: 16px; }
            .field { margin: 12px 0; }
            .label { font-weight: bold; color: #444; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
            .value { padding: 8px 12px; border-left: 3px solid #f97316; background: #fafafa; font-size: 13px; }
            .empty { color: #aaa; font-style: italic; }
            .footer { margin-top: 32px; text-align: center; font-size: 11px; color: #888; border-top: 1px solid #ccc; padding-top: 12px; }
          </style>
        </head>
        <body>
          <h1>Communications Plan</h1>
          <div class="meta"><strong>Exercise:</strong> ${esc(currentExercise?.exercise_name || 'N/A')} &nbsp;·&nbsp; <strong>Generated:</strong> ${new Date().toLocaleString()}</div>
          ${section('Basic Information', FIELDS)}
          ${section('Channels and Interoperability', CHANNEL_FIELDS)}
          <div class="footer">Powered by EXRSIM · Emergency Response Simulation Platform</div>
        </body>
      </html>
    `;
    writeSanitizedHTML(w.document, html);
    w.focus();
    setTimeout(() => w.print(), 250);
  };

  if (!exerciseId) {
    return (
      <div className="text-center py-8 text-gray-400">
        Please select an exercise first.
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="comms-plan-brief-module">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${theme?.colors?.textPrimary || 'text-white'} flex items-center gap-2`}>
            <Radio className="h-6 w-6 text-amber-500" />
            ICS 205 - Comms Plan
          </h1>
          <p className="text-sm text-gray-400 mt-1">Descriptive communications plan for the exercise</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-gray-500 mr-2 min-w-[110px] text-right" data-testid="comms-plan-save-status">
            {saving ? (
              <span className="flex items-center gap-1 justify-end"><Loader2 className="h-3 w-3 animate-spin" /> Saving…</span>
            ) : savedAt ? (
              <span>Saved {savedAt.toLocaleTimeString()}</span>
            ) : null}
          </div>
          <Button
            onClick={handleSaveNow}
            variant="outline"
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
            data-testid="comms-plan-save-btn"
          >
            <Save className="h-4 w-4 mr-2" /> Save
          </Button>
          <Button
            onClick={handlePrint}
            className="bg-amber-500 hover:bg-amber-600 text-black"
            data-testid="comms-plan-print-btn"
          >
            <Printer className="h-4 w-4 mr-2" /> Print Comms Plan
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        </div>
      ) : (
        <>
          <Card className="bg-gray-900 border-gray-700">
            <CardHeader className="py-2">
              <CardTitle className="flex items-center gap-2 text-amber-500">
                <FileText className="h-5 w-5" /> Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-2">
              {FIELDS.map(f => (
                <div key={f.key}>
                  <Label className="text-white text-xs font-medium mb-1 block">{f.label}</Label>
                  <Textarea
                    value={data[f.key] || ''}
                    onChange={(e) => onChange(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    rows={3}
                    className="bg-gray-800 border-gray-600 text-white placeholder-gray-500 resize-y"
                    data-testid={`comms-plan-${f.key}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-gray-700">
            <CardHeader className="py-2">
              <CardTitle className="flex items-center gap-2 text-amber-500">
                <Radio className="h-5 w-5" /> Channels and Interoperability
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-2">
              {CHANNEL_FIELDS.map(f => (
                <div key={f.key}>
                  <Label className="text-white text-xs font-medium mb-1 block">{f.label}</Label>
                  <Textarea
                    value={data[f.key] || ''}
                    onChange={(e) => onChange(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    rows={3}
                    className="bg-gray-800 border-gray-600 text-white placeholder-gray-500 resize-y"
                    data-testid={`comms-plan-${f.key}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Plan Entries — time-bounded Title/Date-Time/Plan cards (moved from ICS 205A Contact List) */}
          <PlanEntriesCard
            formType="ics_205a"
            exerciseId={exerciseId || ''}
            title="ICS 205A — Plan Entries"
            description="Communications plan entries — each with a Title, a 24-hour Date/Time, a Phase, and a free-text Plan"
            cardClass="bg-gray-900 border-gray-700"
            currentPhase={currentPhase}
            showToDateTime={false}
            dateTimeLabel="Date/Time"
          />
        </>
      )}
    </div>
  );
};

export default CommsPlanBriefModule;
